/* eslint-disable max-len */
import { ReactNode } from "react";
import { Box, Button, SvgIcon, SvgIconProps, TextField, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import PopupDialog from "../../components/PopupDialog";
import { AppRoutes } from "../../config";
import { LandToken } from "../../types/LandToken";
import { landImageSrc } from "../../utils/landImage";
import { ClickableContainer } from "../../utils/table-schemas";

// The V9 offer / bid / cancel-listing popups (Figma node 3001:4019). All three are the same
// composition — a square land card above a purple panel carrying the current high-water mark,
// an optional amount field and one CTA — so they share this shell and supply only the panel's
// body as `children`.
//
// The list-to-offer, mint (TreeClaimModalNew), cancel-auction and transfer-guardian popups
// (Figma node 3002:4045) sit on the same shell. Their panels carry a few more lines than the
// offer/bid trio — the stats their old frosted panels showed — so the shell takes an optional
// `rows` block above the heading. Nothing about the trio changes when `rows` is absent.
//
// Before this, each popup was written twice in its call site (a SwipeableDrawer copy for mobile
// and a Dialog copy for desktop, ~95% identical). The responsive split now lives once, in
// PopupDialog, which also carries the z-index fix the land page needs: the header is sticky at
// `zIndex.drawer + 1`, so a bare Drawer gets its top painted over.

// Exported Figma assets, inlined so they survive the 7-day expiry on Figma's asset URLs. These
// are plain SvgIcon wrappers rather than createSvgIcon because each keeps its own exported
// viewBox (14x14, 19x20) — createSvgIcon hard-codes 0 0 24 24 and would rescale the glyphs.
const ArrowIcon = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 14 14" {...props}>
    <path
      d="M7 2.33333L6.1775 3.15583L9.4325 6.41667H2.33333V7.58333H9.4325L6.1775 10.8442L7 11.6667L11.6667 7L7 2.33333Z"
      fill="currentColor"
    />
  </SvgIcon>
);

// const BellPlusIcon = (props: SvgIconProps) => (
//   <SvgIcon viewBox="0 0 19 20" {...props}>
//     <path
//       d="M8 20C7.45 20 6.97917 19.8042 6.5875 19.4125C6.19583 19.0208 6 18.55 6 18H10C10 18.55 9.80417 19.0208 9.4125 19.4125C9.02083 19.8042 8.55 20 8 20ZM14 11V8H11V6H14V3H16V6H19V8H16V11H14ZM0 17V15H2V8C2 6.61667 2.41667 5.3875 3.25 4.3125C4.08333 3.2375 5.16667 2.53333 6.5 2.2V1.5C6.5 1.08333 6.64583 0.729167 6.9375 0.4375C7.22917 0.145833 7.58333 0 8 0C8.41667 0 8.77083 0.145833 9.0625 0.4375C9.35417 0.729167 9.5 1.08333 9.5 1.5V2.2C9.73333 2.26667 9.9625 2.3375 10.1875 2.4125C10.4125 2.4875 10.625 2.58333 10.825 2.7C10.575 2.93333 10.35 3.1875 10.15 3.4625C9.95 3.7375 9.775 4.03333 9.625 4.35C9.375 4.23333 9.1125 4.14583 8.8375 4.0875C8.5625 4.02917 8.28333 4 8 4C6.9 4 5.95833 4.39167 5.175 5.175C4.39167 5.95833 4 6.9 4 8V15H12V12.2C12.3 12.3833 12.6167 12.5333 12.95 12.65C13.2833 12.7667 13.6333 12.8583 14 12.925V15H16V17H0Z"
//       fill="currentColor"
//     />
//   </SvgIcon>
// );

// const IdleIcon = (props: SvgIconProps) => (
//   <SvgIcon viewBox="0 -960 960 960" {...props}>
//     <path d="M140-440q-25 0-42.5-17.5T80-500q0-25 17.5-42.5T140-560h240q25 0 42.5 17.5T440-500q0 25-17.5 42.5T380-440H140Zm440 0q-25 0-42.5-17.5T520-500q0-25 17.5-42.5T580-560h240q25 0 42.5 17.5T880-500q0 25-17.5 42.5T820-440H580Z" />
//   </SvgIcon>
// );

const PANEL_PURPLE = "#BC92F8";

// The theme sets MuiButton `sizeLarge` to a fixed 180px / `min-width: max-content`, and MUI
// injects that class after a styled() wrapper — so at equal specificity the theme wins and a
// plain `width: 100%` is ignored. The doubled `&&` outranks it without an inline style.
const fullWidthOverTheme = {
  "&&": { width: "100%", minWidth: 0, maxWidth: "100%" },
} as const;

/** The `#F2F2F2` amount field in the purple panel. */
export const PopupValuePill = styled(TextField)({
  ...fullWidthOverTheme,
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: 40,
    borderRadius: 20,
    backgroundColor: "#F2F2F2",
    padding: "0 16px",
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": { border: "none" },
    // Strict-Figma keeps no error text, so the field itself has to carry the invalid state.
    "&.Mui-error": { outline: "1px solid #B00020", outlineOffset: -1 },
    "& input": {
      padding: 0,
      textAlign: "center",
      fontFamily: "'IBM Plex Mono', monospace",
      fontSize: 14,
      fontWeight: 700,
      lineHeight: "19.2px",
      color: "black",
      textTransform: "uppercase",
    },
  },
});

/** The black CTA at the foot of the purple panel. */
export const PopupCTA = styled(Button)({
  ...fullWidthOverTheme,
  minHeight: 44,
  height: "auto",
  backgroundColor: "black",
  color: "white",
  borderRadius: 20,
  fontFamily: "'Inter', sans-serif",
  fontSize: 16,
  fontWeight: 400,
  lineHeight: "28px",
  textTransform: "none",
  border: "none",
  padding: "8px 24px",
  whiteSpace: "normal",
  "&:hover": { backgroundColor: "#333", border: "none", color: "white" },
  // Undrawn states, taken from the house popup-button spec rather than invented.
  "&.Mui-disabled": { backgroundColor: "rgba(0, 0, 0, 0.4)", color: "rgba(255, 255, 255, 0.6)" },
});

/**
 * The value pill re-cut for a wallet address (the transfer-guardian popup). The amount pill
 * centres and upper-cases 14px bold, which suits "$6,000" and mangles "0x…": a 42-character
 * address is ~350px in that face against ~230px of usable pill, and upper-casing it hides the
 * EIP-55 checksum casing the user is checking against. Left-aligned 12px medium fits ~34
 * characters; the native input scrolls the rest with the caret, which beats truncating what
 * the user is about to sign.
 */
export const PopupAddressPill = styled(PopupValuePill)({
  "& .MuiOutlinedInput-root": {
    padding: "0 14px",
    "& input": {
      textAlign: "left",
      textTransform: "none",
      fontSize: 12,
      fontWeight: 500,
      letterSpacing: 0,
    },
  },
});

/**
 * The one line of copy a panel may carry under its CTA — a transaction step, a chain error,
 * the "bidders won't be charged" reassurance. Kept small and centred so the CTA stays the
 * panel's foot visually; `tone="error"` is the pill's outline red, so an invalid address and
 * its message agree.
 */
export const PopupFootnote = styled(Typography, { shouldForwardProp: (prop) => prop !== "tone" })<{
  tone?: "muted" | "error";
}>(({ tone }) => ({
  fontFamily: "Inter, sans-serif",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "16px",
  textAlign: "center",
  color: tone === "error" ? "#B00020" : "rgba(0, 0, 0, 0.7)",
  // Each panel already spaces its children by 24px; the footnote sits closer to the CTA it
  // explains than to the next module.
  marginTop: -12,
}));

/**
 * `CountdownTimer` paints its digits with the app's `Text` colours, which are for a white
 * page; inside the purple panel a row's timer has to be black and one size with the row's
 * other values. Same `.timerItem_over span` hook the old frosted panels overrode to white.
 */
export const popupTimerSx = {
  "& .timerItem_over span": {
    fontFamily: "'IBM Plex Mono', monospace !important",
    fontSize: "12px !important",
    lineHeight: "16px !important",
    color: "black !important",
    fontWeight: "400 !important",
  },
} as const;

// A secondary row's two halves. Same mono face as the heading, two sizes down and regular
// weight, so the rows read as detail under it and the heading keeps the figure's emphasis.
const RowLabel = styled(Typography)({
  fontFamily: "'IBM Plex Mono', monospace",
  fontWeight: 400,
  fontSize: 12,
  lineHeight: "16px",
  color: "rgba(0, 0, 0, 0.7)",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
});

// A div, not Typography: a row's value may be the countdown (a flex row of its own spans),
// and Typography's <p> can't legally wrap that.
const RowValue = styled("div")({
  fontFamily: "'IBM Plex Mono', monospace",
  fontWeight: 400,
  fontSize: 12,
  lineHeight: "16px",
  color: "black",
  textAlign: "right",
  textTransform: "uppercase",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const HeroCard = styled(Box, { shouldForwardProp: (prop) => prop !== "imageUrl" })<{ imageUrl?: string }>(
  ({ theme, imageUrl }) => ({
    position: "relative",
    width: "100%",
    aspectRatio: "1",
    backgroundImage: imageUrl ? `url('${imageUrl}')` : "none",
    backgroundColor: "#2b3501",
    backgroundSize: "cover",
    backgroundPosition: "center",
    flexShrink: 0,
    // All four corners, and the panel below rounds its own — the two are separate cards sitting
    // flush, so the seam pinches inward on both sides. That pinch is the design (measured off
    // the Figma render: at the join both edges inset by the full 20px radius), not an artifact.
    borderRadius: 20,
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      // A square hero would push the CTA off a short phone; cap it so the panel stays in view.
      maxHeight: "45dvh",
    },
  })
);

const PurplePanel = styled(Box)({
  backgroundColor: PANEL_PURPLE,
  display: "flex",
  flexDirection: "column",
  gap: 24,
  padding: 24,
  width: "100%",
  boxSizing: "border-box",
  // All four corners at every width: the hero and the panel are two separate modules in the
  // Figma, each fully rounded. Squaring the panel's foot on phones (an earlier attempt to sit
  // flush with the viewport edge) stopped it reading as a module of its own.
  borderRadius: 20,
});

// const StatLabel = styled(Typography)({
//   fontFamily: "'IBM Plex Mono', monospace",
//   fontWeight: 700,
//   fontSize: 16,
//   lineHeight: 1.3,
//   color: "rgba(255,255,255,0.5)",
// });

// const StatValue = styled(Typography)({
//   fontFamily: "'IBM Plex Mono', monospace",
//   fontWeight: 700,
//   fontSize: 16,
//   lineHeight: 1.3,
//   color: "#fff",
// });

/** One secondary label/value line in the purple panel — see `rows` below. */
export interface PopupStatRow {
  label: string;
  value: ReactNode;
}

export interface LandActionPopupProps {
  open: boolean;
  onClose: () => void;
  token?: LandToken;
  /** "HIGHEST OFFER" | "CURRENT BID" | "OPENING BID" — the purple panel's heading. */
  statLabel?: string;
  /**
   * Already-formatted amount, e.g. "$6,000". Optional: the transfer popup's heading is a
   * caption over its address field and carries no figure.
   */
  statValue?: string;
  /** Highest offerer / bidder. Omitted when there is no one yet. */
  statAddress?: string | null;
  /**
   * Secondary stats (mint capacity, contract period, …) drawn ABOVE the heading, under a
   * hairline. The offer / bid popups keep none — their heading is the only figure — so the
   * block is absent there and they render exactly as before. The list-to-offer, mint,
   * cancel-auction and transfer popups carry the rows their old frosted panels did; the
   * heading below them is the headline figure (LAST SOLD FOR, AVAILABLE MINT, CURRENT BID),
   * which is the same rows-then-highlight order those panels used.
   */
  rows?: PopupStatRow[];
  /** The panel body under the heading — value pill and CTA. */
  children: ReactNode;
}

const truncateAddress = (address: string) =>
  address.length > 12 ? `${address.slice(0, 6)}…${address.slice(-4).toUpperCase()}` : address;

const LandActionPopup = ({
  open,
  onClose,
  token,
  statLabel,
  statValue,
  statAddress,
  rows,
  children,
}: LandActionPopupProps) => {
  // const rawLocation = token?.meta?.location;
  // const location = rawLocation ? (typeof rawLocation === "string" ? JSON.parse(rawLocation) : rawLocation) : {};

  // const ecoScore = token?.ecoScore != null ? (token.ecoScore * 100).toFixed(0) : "N/A";

  // Pre-mint tokens have `etNft === null`, and formatValue(undefined) throws on .toLocaleString —
  // every read below is guarded for that. Mirrors LandCardPopup.
  // const totalTrees =
  //   token?.etNft?.totalTreeForEasement != null ? formatValue(token.etNft.totalTreeForEasement as any, false) : null;
  // const formattedTrees = totalTrees != null ? sixDigits(formatValue(totalTrees as any, true, 0)) : null;
  // const remainingTrees = token?.etNft?.easementActivated
  //   ? Number(formatValue(token.etNft.totalTreeForEasement as any, false)) -
  //     Number(formatValue(token.etNft.easementActivated.rewardClaimed as any, false) || 0)
  //   : null;
  // const formattedRemainingTrees = remainingTrees !== null ? String(numberWithCommas(sixDigits(remainingTrees))) : null;
  // const tokenNumber = token?.etNft?.id != null ? String(token.etNft.id) : null;

  // const publicStatus = token
  //   ? resolveStandardStatus(
  //       { status: token.status, etNft: token.etNft, offerStatus: token.offerStatus, meta: token.meta },
  //       { role: "public" }
  //     )
  //   : null;
  // Chip colours are the Figma's (3001:3926 live / 3001:3916 idle), which deliberately differ
  // from the app-wide chip LandCardPopup paints. Only this popup uses them.
  // const isGreenBadge = publicStatus?.color === "action.pulse";
  // const badgeBg = isGreenBadge ? "rgba(43, 53, 1, 0.25)" : "rgba(43,53,1,0.25)";
  // const badgeBorderColor = isGreenBadge ? "#CF9" : "#000000";
  // const badgeTextColor = isGreenBadge ? "#CF9" : "#000000";

  // const stats = [
  //   { label: "SCORE", value: ecoScore },
  //   { label: "TR3", value: formattedRemainingTrees ?? formattedTrees ?? "N/A" },
  //   { label: "No", value: tokenNumber ?? "N/A" },
  // ];

  // 310px is the drawn card width (Figma 3001:3908), not a rounded-up guess — at 360 the 20px
  // corners read proportionally tighter than the design.
  return (
    <PopupDialog open={open} onClose={onClose} surface="plain" maxWidth={310}>
      {/* No clip and no radius here: the hero and the panel are two separately rounded cards
          stacked flush (Figma 3001:3908 / 3001:3958, both 20px on all four corners). Clipping
          them under one rounded container squares the seam, which is visibly not the design. */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        {/* `poster` is the ≤768px rendition. The full-size land images are 1.5–77MB animated
            GIFs, which stall a phone if used for a card this small. */}
        <HeroCard imageUrl={landImageSrc(token, "poster")}>
          {/* {token?.etNft?.editionId && (
            <Box sx={{ position: "absolute", top: 18, left: 18, zIndex: 2 }}>
              <EditionBadgeCanvas
                size={48}
                editionId={Number(token.etNft.editionId)}
                active={remainingTrees !== null && remainingTrees > 0}
              />
            </Box>
          )} */}

          {/* {publicStatus && (
            <Box
              sx={{
                position: "absolute",
                top: 21,
                right: 21,
                zIndex: 2,
                height: 31,
                px: "14px",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                backgroundColor: badgeBg,
                border: `1px solid ${badgeBorderColor}`,
                borderRadius: "20px",
                color: badgeTextColor,
              }}
            >
              {publicStatus.label === "Active" && <PulseCircle sold={true} sx={{ mr: 0 }} />}
              {publicStatus.label === "Pending" && <PulseCircle warning sx={{ mr: 0 }} />}
              {(publicStatus.label === "Offer" || publicStatus.label === "Auction") && (
                <ListAltOutlinedIcon sx={{ fontSize: 14 }} />
              )}
              {publicStatus.label === "Idle" && <IdleIcon sx={{ fontSize: 14 }} />}
              <Typography
                sx={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontWeight: 700,
                  fontSize: 12,
                  color: badgeTextColor,
                  textTransform: "uppercase",
                  lineHeight: 1,
                }}
              >
                {publicStatus.label}
              </Typography>
            </Box>
          )} */}

          {/* Blur scrim. The Figma covers the WHOLE card with it (node 3001:3909 is a full
              310x310 rect, blur 15 + rgba(43,53,1,0.15)) — no gradient mask. An earlier version
              here carried LandCardPopup's bottom-fading mask, which left the top sharp and made
              the card read quite differently from the design. */}
          {/* <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(43, 53, 1, 0.15)",
              backdropFilter: "blur(15px)",
              WebkitBackdropFilter: "blur(15px)",
              maskImage: "linear-gradient(to bottom, transparent 0%, black 85%)",
              borderRadius: "20px",
            }}
          /> */}

          {/* One bottom-anchored column. Offsets measured off the Figma vectors relative to the
              310px card: 24 bottom inset, stats 44.5 tall, 10.4 gap, location 15, 2.6 gap,
              title 30 — which puts the title top at 183.5 against the Figma's 184. */}
          {/* <Box sx={{ position: "absolute", bottom: 24, left: 24, right: 24, zIndex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 700,
                    fontSize: 26,
                    letterSpacing: "-1.04px",
                    lineHeight: "28.6px",
                    color: "#fff",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {token?.meta?.name ?? ""}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 900,
                    fontSize: 12,
                    lineHeight: "15px",
                    marginTop: "2.6px",
                    color: "#fff",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {location?.name ?? ""}
                  {location?.country ? `, ${location.country}` : ""}
                </Typography>
              </Box>
              <Box
                aria-hidden
                sx={{
                  width: 34,
                  height: 34,
                  flexShrink: 0,
                  borderRadius: "50%",
                  backgroundColor: "rgba(217, 217, 217, 0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none",
                }}
              >
                <BellPlusIcon sx={{ width: 17, height: 18, color: "#fff" }} />
              </Box>
            </Box>

            <Box
              sx={{
                marginTop: "10.4px",
                display: "flex",
                justifyContent: "space-between",
                gap: "8px",
              }}
            >
              {stats.map((stat) => (
                <Box key={stat.label} sx={{ minWidth: 0 }}>
                  <StatLabel>{stat.label}</StatLabel>
                  <StatValue>{stat.value}</StatValue>
                </Box>
              ))}
            </Box>
          </Box> */}
        </HeroCard>

        <PurplePanel>
          {rows && rows.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {rows.map((row) => (
                <Box
                  key={row.label}
                  sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}
                >
                  <RowLabel>{row.label}</RowLabel>
                  <RowValue>{row.value}</RowValue>
                </Box>
              ))}
              {/* Hairline between the detail rows and the headline figure — the old frosted
                  panels drew the same rule, at 30% white on their dark glass; on the purple
                  it is 15% black. The 6px gap above plus the panel's own 24px below it keep
                  the rule nearer the rows it closes than the heading it precedes. */}
              {/* "1px", not 1 — a bare 1 is 100% in the sx height scale. */}
              <Box aria-hidden sx={{ height: "1px", backgroundColor: "rgba(0, 0, 0, 0.15)", marginTop: "6px" }} />
            </Box>
          )}
          {statLabel && (
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
              <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontWeight: 700,
                    fontSize: 13.8,
                    lineHeight: "21px",
                    color: "black",
                    textTransform: "uppercase",
                  }}
                >
                  {statLabel}
                </Typography>
                {statAddress && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                    {/* The arrow is decoration and stays outside the hit area — only the address
                      itself is the link. */}
                    <ArrowIcon sx={{ width: 14, height: 14, flexShrink: 0, color: "black" }} />
                    {/* The offerer / bidder's wallet, matching where AddressLink sends a
                      `type="user"` address. ClickableContainer supplies the hover and press
                      states; its padding and equal negative margin cancel out, so the text sits
                      where it did and only the highlight bleeds past it — the 35px header block
                      and the 10px gap off the arrow are both unchanged. Closing on click leaves
                      the popup's scroll lock and focus trap to unwind normally rather than being
                      torn down mid-navigation. */}
                    <RouterLink
                      to={AppRoutes.UserWallet.path.replace(":address", statAddress.toLowerCase())}
                      onClick={onClose}
                      style={{ textDecoration: "none", color: "inherit", minWidth: 0 }}
                    >
                      <ClickableContainer sx={{ alignItems: "center", minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontFamily: "Inter, sans-serif",
                            fontWeight: 400,
                            fontSize: 14,
                            lineHeight: "14px",
                            color: "black",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {truncateAddress(statAddress)}
                        </Typography>
                      </ClickableContainer>
                    </RouterLink>
                  </Box>
                )}
              </Box>
              {statValue !== undefined && (
                <Typography
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 700,
                    fontSize: 14,
                    lineHeight: "16.8px",
                    color: "black",
                    textAlign: "right",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                  }}
                >
                  {statValue}
                </Typography>
              )}
            </Box>
          )}
          {children}
        </PurplePanel>
      </Box>
    </PopupDialog>
  );
};

export default LandActionPopup;
